import axios from "axios";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { ISoftwareVersionsRepository, SoftwareVersion, SupportedSoftware, UpdateSoftwareVersionPayload } from "@domain/repositories/ISoftwareVersionsRepository";
import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";

/**
Parses the leading major version out of "20.6.260528.94842" or "SN 20.6...".
*/
const parseMajorVersion = (version: string): number => {
  const match = /(\d+)/.exec(version.trim().replace(/^(?:SN|SM)\s*/i, ""));
  return match ? Number(match[1]) : 0;
};

@injectable()
export class SoftwareVersionsRepository implements ISoftwareVersionsRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  /**
   * The legacy endpoint returns plain strings, so enablement and default flags are
   * unknown. Every version is reported as enabled and none as default, which is the
   * correct read for a manager that has no concept of either.
   */
  private async listLegacyAsync(): Promise<SoftwareVersion[]> {
    const legacyUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareVersionsLegacyList}`;
    const versions = await this._requestHandlerService.handleRequestAsync<string[]>(() => axios.get<string[]>(legacyUrl));

    if (!Array.isArray(versions)) {
      return [];
    }

    return versions
      .filter((version): version is string => typeof version === "string" && version.trim().length > 0)
      .map((version) => ({
        version: version.trim(),
        majorVersion: parseMajorVersion(version),
        enabled: true,
        isDefault: false,
      }));
  }

  private isNotFound(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      return error.response?.status === 404;
    }

    const status = (error as { status?: number; response?: { status?: number } } | null)?.response?.status ?? (error as { status?: number } | null)?.status;
    return status === 404;
  }

  async ListAsync(isEnabled?: boolean, software?: SupportedSoftware): Promise<SoftwareVersion[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareVersions}`;
    const parameters = {
      ...(isEnabled !== undefined && { enabled: isEnabled }),
      ...(software && { software }),
    };

    try {
      return await this._requestHandlerService.handleRequestAsync<SoftwareVersion[]>(() => axios.get<SoftwareVersion[]>(url, { params: parameters }));
    } catch (error) {
      if (!this.isNotFound(error)) {
        throw error;
      }

      // Older managers expose only the legacy string listing. Falling back keeps an
      // unreleased UI usable on top of a released chart, which is exactly how the
      // demo-preview environment is meant to run.
      return this.listLegacyAsync();
    }
  }

  async UpdateAsync(version: string, payload: UpdateSoftwareVersionPayload, software: SupportedSoftware = "Spectronaut"): Promise<SoftwareVersion> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.softwareVersionsVersion}`.replace("{version}", () => encodeURIComponent(version));
    return this._requestHandlerService.handleRequestAsync<SoftwareVersion>(() => axios.patch<SoftwareVersion>(url, payload, { params: { software } }));
  }
}
